import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Shield, Search, Users, AlertCircle, Check, X, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: string;
  email: string;
  full_name: string;
  role_level?: number;
  organizations: {
    id: string;
    role: {
      id: string;
      name: string;
      level: number;
    };
  }[];
}

const UserAuthManager: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  // Check if current user is super admin
  const { data: isSuperAdmin } = useQuery({
    queryKey: ['isSuperAdmin', currentUser?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_organizations')
        .select('role:user_roles(level)')
        .eq('user_id', currentUser?.id)
        .eq('status', 'active')
        .single();

      if (error) throw error;
      return data?.role?.level === 1;
    },
    enabled: !!currentUser?.id
  });

  // Fetch users with their roles
  const { data: users, isLoading } = useQuery({
    queryKey: ['users', searchTerm],
    queryFn: async () => {
      const query = supabase
        .from('users')
        .select(`
          id,
          email,
          full_name,
          organizations:user_organizations(
            id,
            role:user_roles(
              id,
              name,
              level
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (searchTerm) {
        query.or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as User[];
    },
    enabled: !!isSuperAdmin
  });

  // Update user role mutation
  const updateUserRole = useMutation({
    mutationFn: async ({ userId, organizationId, roleId }: { 
      userId: string;
      organizationId: string;
      roleId: string;
    }) => {
      const { error } = await supabase
        .from('user_organizations')
        .update({ role_id: roleId })
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User role updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update user role');
      console.error('Role update error:', error);
    }
  });

  if (!isSuperAdmin) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-error mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">
          You do not have permission to access this area.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Authorization Management</h1>
          <p className="mt-1 text-gray-600">
            Manage user roles and permissions across the platform
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center">
                    <RefreshCw className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
                  </td>
                </tr>
              ) : users?.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users?.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.organizations?.map((org) => (
                        <div key={org.id} className="text-sm text-gray-900">
                          {org.role?.name} (Level {org.role?.level})
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <select
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        value={user.organizations?.[0]?.role?.id}
                        onChange={(e) => {
                          if (user.organizations?.[0]) {
                            updateUserRole.mutate({
                              userId: user.id,
                              organizationId: user.organizations[0].id,
                              roleId: e.target.value
                            });
                          }
                        }}
                      >
                        <option value="1">Super Administrator (Level 1)</option>
                        <option value="2">IMO Administrator (Level 2)</option>
                        <option value="3">Agency Administrator (Level 3)</option>
                        <option value="4">Standard User (Level 4)</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserAuthManager;